// Settle sheet — slides up *inside* the drawer.
// One amount + a method dropdown. Form morphs by method. Maps to the four
// BE write endpoints from the audit:
//   card_on_file / invoice → /payment-requests
//   remind_invoice         → /invoices/{id}/remind
//   void_invoice           → /invoices/{id}/reconcile
//   cash / external        → /payments (external sets create_charge=true)
//   write_off              → /write-offs

import { useEffect, useMemo, useState } from 'react';
import {
  type DisplayEncounterChargeGroup,
} from '@features/billing/components/charge-groups';
import { useRecordPayment, useWriteOff } from '@features/billing/store/hooks';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import {
  usePatientPaymentMethods,
  useReconcileInvoice,
  useSendInvoiceReminder,
  useSendPaymentRequest,
} from '@features/payments/store/hooks';
import { usePatientById } from '@hooks/usePatientById';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Slide,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import type { SettleMethod } from '../types';

interface Props {
  patientId: string;
  patientName?: string | null;
  encounterId?: string | null;
  initialMethod?: SettleMethod;
  initialAmountCents: number;
  /**
   * BE-synthesized default for the "Reason (shown on invoice)" field, from
   * /preview's `suggested_reason`. Used as the prefill for invoice + card
   * methods. User can edit before sending.
   */
  suggestedReason?: string;
  /** Exact charge lines safe to place on a newly-created invoice. */
  invoiceChargeLineIds?: string[];
  /** Exact charge lines safe to write off. Includes invoiced lines. */
  writableChargeLineIds?: string[];
  /** Sum of invoiceable receipt lines represented by invoiceChargeLineIds. */
  invoiceableAmountCents?: number;
  /** Preview-derived encounter/charge-line groups for settlement selection. */
  chargeGroups?: DisplayEncounterChargeGroup[];
  /** First open-invoice id on this patient — required for `remind_invoice`. */
  reminderInvoiceId?: string;
  source?: string;
  onClose: () => void;
}

interface MethodOption {
  value: SettleMethod;
  label: string;
  sub: string;
  /** When true, hide from the dropdown for the current scope. */
  unavailable?: boolean;
}

export const SettleSheet = ({
  patientId,
  patientName,
  encounterId,
  initialMethod,
  initialAmountCents,
  suggestedReason,
  invoiceChargeLineIds,
  writableChargeLineIds,
  invoiceableAmountCents,
  chargeGroups = [],
  reminderInvoiceId,
  source,
  onClose,
}: Props) => {
  const isEncounterScope = !!encounterId;
  const canCreateInvoice =
    !Array.isArray(invoiceChargeLineIds) || invoiceChargeLineIds.length > 0;
  const paymentRequestChargeLineIds =
    invoiceChargeLineIds && invoiceChargeLineIds.length > 0
      ? invoiceChargeLineIds
      : undefined;
  const startingMethod =
    (initialMethod === 'invoice' || initialMethod === 'card_on_file') && !canCreateInvoice
      ? reminderInvoiceId
        ? 'remind_invoice'
        : 'cash'
      : initialMethod;
  const { enqueueSnackbar } = useSnackbar();
  const { payment } = useEventTracking();

  const [method, setMethod] = useState<SettleMethod>(startingMethod ?? 'cash');
  const [amountStr, setAmountStr] = useState<string>(
    initialAmountCents > 0 ? (initialAmountCents / 100).toFixed(2) : '',
  );
  const [amountTouched, setAmountTouched] = useState(false);
  // Method-specific form state — kept narrow; we only persist what's submitted.
  const [paymentMethodKind, setPaymentMethodKind] = useState<'cash' | 'check'>('cash');
  const [externalKind, setExternalKind] = useState<'square' | 'venmo' | 'other_pos'>('square');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [collectedAt, setCollectedAt] = useState('');
  const [note, setNote] = useState('');
  const [invoiceReason, setInvoiceReason] = useState(suggestedReason ?? '');
  const [invoiceReasonTouched, setInvoiceReasonTouched] = useState(false);
  const [mobileOverride, setMobileOverride] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [writeOffReason, setWriteOffReason] =
    useState<'hardship' | 'bad_debt' | 'courtesy' | 'other'>('courtesy');
  const [selectedChargeLineIds, setSelectedChargeLineIds] = useState<string[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [cardOnFileId, setCardOnFileId] = useState<string>('');

  // Patient mobile_phone — fetched async, used to prefill the invoice's
  // mobile-override field. Same await-and-fill pattern as suggestedReason.
  const { patient } = usePatientById(patientId);

  // Saved cards on file. Drives:
  //   - whether the card_on_file method is offered at all
  //   - the card picker shown when card_on_file is selected
  //   - which Stripe payment_method_id we pass on submit
  const { paymentMethods, isStripeEnabled } = usePatientPaymentMethods(patientId);
  const savedCards = useMemo(
    () => paymentMethods.filter((pm) => pm.type === 'card' || pm.card_brand),
    [paymentMethods],
  );
  const defaultCard = savedCards.find((pm) => pm.is_default) ?? savedCards[0];

  useEffect(() => {
    if (!cardOnFileId && defaultCard?.id) setCardOnFileId(defaultCard.id);
  }, [cardOnFileId, defaultCard?.id]);

  // /preview returns asynchronously, so the suggestedReason may not be
  // available at mount. Fill the reason field in once it arrives, but only
  // if the user hasn't typed in it yet.
  useEffect(() => {
    if (suggestedReason && !invoiceReason && !invoiceReasonTouched) {
      setInvoiceReason(suggestedReason);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedReason, invoiceReasonTouched]);

  useEffect(() => {
    if (patient?.mobile_phone && !mobileOverride) {
      setMobileOverride(patient.mobile_phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.mobile_phone]);

  const { recordPayment } = useRecordPayment();
  const { writeOff } = useWriteOff();
  const { mutate: sendPaymentRequest } = useSendPaymentRequest();
  const { sendReminder } = useSendInvoiceReminder();
  const { reconcileInvoice } = useReconcileInvoice();

  const methodOptions: MethodOption[] = [
    {
      value: 'void_invoice',
      label: 'Void invoice',
      sub: reminderInvoiceId ? 'Release linked charge lines' : 'No open invoice',
      unavailable: !reminderInvoiceId,
    },
    {
      value: 'card_on_file',
      label: 'Charge card on file',
      sub: defaultCard
        ? canCreateInvoice
          ? formatCard(defaultCard)
          : 'Covered by open invoice'
        : isStripeEnabled
          ? 'No card on file'
          : 'Stripe Connect not set up',
      unavailable: !defaultCard || !canCreateInvoice,
    },
    {
      value: 'invoice',
      label: 'Send invoice',
      sub: canCreateInvoice ? 'Stripe link via SMS' : 'Covered by open invoice',
      unavailable: !canCreateInvoice,
    },
    {
      value: 'remind_invoice',
      label: 'Resend invoice',
      sub: reminderInvoiceId ? 'Existing open invoice' : 'No open invoice',
      unavailable: !reminderInvoiceId,
    },
    { value: 'cash', label: 'Cash / check', sub: 'Recorded manually' },
    {
      value: 'external',
      label: 'External collection',
      sub: 'Square / POS / Venmo',
    },
    { value: 'write_off', label: 'Write off', sub: 'Adjustment, no money moves' },
  ];

  const cents = Math.round((Number(amountStr) || 0) * 100);
  const requiresAmount = method !== 'remind_invoice' && method !== 'void_invoice';
  const isInvoiceLikeMethod = method === 'invoice' || method === 'card_on_file';
  const isChargeLineSelectionMethod =
    method === 'invoice' || method === 'card_on_file' || method === 'write_off';
  const selectableChargeLineIds = useMemo(() => {
    const ids = method === 'write_off' ? writableChargeLineIds : invoiceChargeLineIds;
    return ids && ids.length > 0 ? ids : [];
  }, [invoiceChargeLineIds, method, writableChargeLineIds]);
  const selectableChargeLineIdSet = useMemo(
    () => new Set(selectableChargeLineIds),
    [selectableChargeLineIds],
  );
  const selectedChargeLineIdSet = useMemo(
    () => new Set(selectedChargeLineIds),
    [selectedChargeLineIds],
  );
  const selectedChargeLineAmountCents = useMemo(() => {
    let total = 0;
    for (const group of chargeGroups) {
      for (const line of group.lines) {
        if (selectedChargeLineIdSet.has(line.id)) total += line.amountCents;
      }
    }
    return total;
  }, [chargeGroups, selectedChargeLineIdSet]);
  const selectedInvoiceReason = useMemo(
    () => buildSelectedInvoiceReason(chargeGroups, selectedChargeLineIdSet),
    [chargeGroups, selectedChargeLineIdSet],
  );
  const selectedPaymentRequestChargeLineIds =
    isInvoiceLikeMethod && selectedChargeLineIds.length > 0
      ? selectedChargeLineIds
      : paymentRequestChargeLineIds;
  const selectedWriteOffChargeLineIds =
    method === 'write_off' && selectedChargeLineIds.length > 0
      ? selectedChargeLineIds
      : undefined;
  const hasSelectedReceiptLines = !!selectedPaymentRequestChargeLineIds?.length;
  const expectedInvoiceLineAmountCents =
    selectedChargeLineAmountCents > 0
      ? selectedChargeLineAmountCents
      : invoiceableAmountCents;
  const invoiceAmountMismatch =
    isInvoiceLikeMethod &&
    hasSelectedReceiptLines &&
    expectedInvoiceLineAmountCents !== undefined &&
    cents !== expectedInvoiceLineAmountCents;
  const writeOffAmountMismatch =
    method === 'write_off' &&
    !!selectedWriteOffChargeLineIds?.length &&
    selectedChargeLineAmountCents > 0 &&
    cents !== selectedChargeLineAmountCents;
  const canSubmit =
    !submitting &&
    (!requiresAmount || cents > 0) &&
    (!isChargeLineSelectionMethod ||
      selectedChargeLineIds.length > 0 ||
      selectableChargeLineIds.length === 0) &&
    ((method !== 'remind_invoice' && method !== 'void_invoice') ||
      !!reminderInvoiceId);

  useEffect(() => {
    if (!isChargeLineSelectionMethod) return;
    setSelectedChargeLineIds(selectableChargeLineIds);
  }, [isChargeLineSelectionMethod, selectableChargeLineIds]);

  useEffect(() => {
    if (!isInvoiceLikeMethod || invoiceReasonTouched || !selectedInvoiceReason) return;
    setInvoiceReason(selectedInvoiceReason);
  }, [invoiceReasonTouched, isInvoiceLikeMethod, selectedInvoiceReason]);

  useEffect(() => {
    if (amountTouched || !requiresAmount) return;
    const nextAmount =
      isChargeLineSelectionMethod && selectedChargeLineAmountCents > 0
        ? selectedChargeLineAmountCents
        : initialAmountCents;
    setAmountStr(nextAmount > 0 ? (nextAmount / 100).toFixed(2) : '');
  }, [
    amountTouched,
    initialAmountCents,
    isChargeLineSelectionMethod,
    requiresAmount,
    selectedChargeLineAmountCents,
  ]);

  const toggleChargeLine = (chargeLineId: string, selected: boolean) => {
    setAmountTouched(false);
    setSelectedChargeLineIds((current) => {
      if (selected) return Array.from(new Set([...current, chargeLineId]));
      return current.filter(id => id !== chargeLineId);
    });
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      if (invoiceAmountMismatch) {
        throw new Error(
          `Invoice amount must match the selected receipt lines: ${dollars(expectedInvoiceLineAmountCents ?? 0)}.`,
        );
      }
      if (writeOffAmountMismatch) {
        throw new Error(
          `Write-off amount must match the selected charge lines: ${dollars(selectedChargeLineAmountCents)}.`,
        );
      }
      switch (method) {
        case 'card_on_file': {
          if (!cardOnFileId) throw new Error('No card on file');
          const result = await sendPaymentRequest({
            patient_id: patientId,
            amount: (cents / 100).toFixed(2),
            reason: invoiceReason || 'Payment',
            encounter_id: encounterId ?? undefined,
            charge_line_ids: selectedPaymentRequestChargeLineIds,
            payment_method_id: cardOnFileId,
            send_notification: false,
            source: source ?? 'ledger_drawer',
          });
          if (!result) throw new Error('Failed to charge card on file');
          payment.requestSent({
            source: source ?? 'ledger_drawer',
            amount: cents,
            delivery_method: 'card_on_file',
          });
          enqueueSnackbar(`Charged ${dollars(cents)} to card on file`, { variant: 'success' });
          break;
        }
        case 'invoice': {
          const result = await sendPaymentRequest({
            patient_id: patientId,
            amount: (cents / 100).toFixed(2),
            reason: invoiceReason || 'Payment',
            encounter_id: encounterId ?? undefined,
            charge_line_ids: selectedPaymentRequestChargeLineIds,
            mobile_phone: mobileOverride || undefined,
            send_notification: sendNotification,
            source: source ?? 'ledger_drawer',
          });
          if (!result) throw new Error('Failed to send invoice');
          payment.requestSent({
            source: source ?? 'ledger_drawer',
            amount: cents,
            delivery_method: sendNotification ? 'sms' : 'silent',
          });
          enqueueSnackbar(`Invoice for ${dollars(cents)} sent`, { variant: 'success' });
          break;
        }
        case 'remind_invoice': {
          if (!reminderInvoiceId) throw new Error('No open invoice to remind');
          await sendReminder(reminderInvoiceId);
          enqueueSnackbar('Reminder sent', { variant: 'success' });
          break;
        }
        case 'void_invoice': {
          if (!reminderInvoiceId) throw new Error('No open invoice to void');
          const result = await reconcileInvoice(reminderInvoiceId, {
            action: 'void_invoice',
            sendNotification: false,
          });
          if (!result) throw new Error('Failed to void invoice');
          enqueueSnackbar('Invoice voided', { variant: 'success' });
          break;
        }
        case 'cash': {
          const result = await recordPayment(patientId, {
            amount_cents: cents,
            payment_method: paymentMethodKind,
            encounter_id: encounterId ?? null,
            reference_number: referenceNumber || null,
            note: note || null,
            collected_at: collectedAt ? new Date(collectedAt).toISOString() : null,
          });
          if (!result.success) {
            throw result.error instanceof Error
              ? result.error
              : new Error('Failed');
          }
          payment.paymentRecorded({
            encounter_id: encounterId ?? undefined,
            payment_method: paymentMethodKind,
            amount_cents: cents,
            allocated: !!encounterId,
          });
          enqueueSnackbar(`Recorded ${dollars(cents)} ${paymentMethodKind} payment`, {
            variant: 'success',
          });
          break;
        }
        case 'external': {
          const result = await recordPayment(patientId, {
            amount_cents: cents,
            payment_method: externalKind,
            encounter_id: encounterId ?? null,
            reference_number: referenceNumber || null,
            note: note || null,
            collected_at: collectedAt ? new Date(collectedAt).toISOString() : null,
            // In encounter scope with no charge_line yet we need the BE to mint
            // the charge_line + post the payment in one call; otherwise the
            // encounter's eligibility badge can't flip.
            create_charge: isEncounterScope ? true : undefined,
          });
          if (!result.success) {
            throw result.error instanceof Error
              ? result.error
              : new Error('Failed');
          }
          payment.paymentRecorded({
            encounter_id: encounterId ?? undefined,
            payment_method: externalKind,
            amount_cents: cents,
            allocated: !!encounterId,
          });
          enqueueSnackbar(`Logged ${dollars(cents)} external collection`, {
            variant: 'success',
          });
          break;
        }
        case 'write_off': {
          const result = await writeOff(patientId, {
            amount_cents: cents,
            reason: writeOffReason,
            note: note || null,
            encounter_id: selectedWriteOffChargeLineIds ? null : encounterId ?? null,
            charge_line_ids: selectedWriteOffChargeLineIds,
          });
          if (!result.success) {
            throw result.error instanceof Error
              ? result.error
              : new Error('Failed');
          }
          enqueueSnackbar(`Wrote off ${dollars(cents)}`, { variant: 'success' });
          break;
        }
      }
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          top: 'auto',
          bottom: 0,
          maxHeight: '85%',
          bgcolor: ELIG_TOKENS.card,
          borderTop: `1px solid ${ELIG_TOKENS.lineStrong}`,
          boxShadow: '0 -8px 24px rgba(28,30,133,0.10)',
          display: 'flex',
          flexDirection: 'column',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <SheetHeader
          title={`Settle ${patientName ?? ''}`.trim()}
          subtitle={isEncounterScope ? 'Encounter scope' : 'Patient scope'}
          onClose={onClose}
        />

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          {isChargeLineSelectionMethod && chargeGroups.length > 0 && (
            <SettlementPreviewPanel
              groups={chargeGroups}
              method={method}
              selectableIds={selectableChargeLineIdSet}
              selectedIds={selectedChargeLineIdSet}
              totalCents={selectedChargeLineAmountCents}
              onToggleLine={toggleChargeLine}
            />
          )}

          {requiresAmount && (
            <TextField
              label="Amount"
              value={amountStr}
              onChange={(e) => {
                setAmountTouched(true);
                setAmountStr(e.target.value);
              }}
              fullWidth
              size="small"
              autoFocus
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                },
              }}
              sx={{ mb: 2 }}
            />
          )}

          <Typography
            variant="overline"
            sx={{ color: ELIG_TOKENS.midnight, fontWeight: 600, display: 'block', mb: 1 }}
          >
            How?
          </Typography>
          <TextField
            select
            value={method}
            onChange={(e) => setMethod(e.target.value as SettleMethod)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          >
            {methodOptions
              .filter((opt) => !opt.unavailable)
              .map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box>
                    <Typography variant="body2">{opt.label}</Typography>
                    <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
                      {opt.sub}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
          </TextField>

          <MethodFields
            method={method}
            isEncounterScope={isEncounterScope}
            paymentMethodKind={paymentMethodKind}
            setPaymentMethodKind={setPaymentMethodKind}
            externalKind={externalKind}
            setExternalKind={setExternalKind}
            referenceNumber={referenceNumber}
            setReferenceNumber={setReferenceNumber}
            collectedAt={collectedAt}
            setCollectedAt={setCollectedAt}
            note={note}
            setNote={setNote}
            invoiceReason={invoiceReason}
            setInvoiceReason={(next) => {
              setInvoiceReasonTouched(true);
              setInvoiceReason(next);
            }}
            mobileOverride={mobileOverride}
            setMobileOverride={setMobileOverride}
            sendNotification={sendNotification}
            setSendNotification={setSendNotification}
            writeOffReason={writeOffReason}
            setWriteOffReason={setWriteOffReason}
            savedCards={savedCards}
            cardOnFileId={cardOnFileId}
            setCardOnFileId={setCardOnFileId}
          />

          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </Box>

        <Box
          sx={{
            borderTop: `1px solid ${ELIG_TOKENS.line}`,
            bgcolor: ELIG_TOKENS.bg,
            px: 3,
            py: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button onClick={onClose} sx={{ color: ELIG_TOKENS.inkSoft, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{
              bgcolor: ELIG_TOKENS.midnight,
              color: '#fff',
              '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.9)' },
              minWidth: 140,
            }}
          >
            {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : primaryFor(method)}
          </Button>
        </Box>
      </Box>
    </Slide>
  );
};

const SettlementPreviewPanel = ({
  groups,
  method,
  selectableIds,
  selectedIds,
  totalCents,
  onToggleLine,
}: {
  groups: DisplayEncounterChargeGroup[];
  method: SettleMethod;
  selectableIds: Set<string>;
  selectedIds: Set<string>;
  totalCents: number;
  onToggleLine: (chargeLineId: string, selected: boolean) => void;
}) => {
  const title = method === 'write_off' ? 'Write-off preview' : 'Invoice preview';
  const subtitle =
    method === 'write_off'
      ? 'What will be marked written off'
      : 'What will appear on this invoice';
  const rows = flattenPreviewRows(groups);
  return (
    <Box
      sx={{
        mb: 2,
        border: `1px solid ${ELIG_TOKENS.lineStrong}`,
        borderRadius: 1,
        bgcolor: ELIG_TOKENS.card,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: ELIG_TOKENS.bg,
          borderBottom: `1px solid ${ELIG_TOKENS.lineStrong}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              color: ELIG_TOKENS.midnight,
              display: 'block',
              fontWeight: 700,
              letterSpacing: 0.4,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkSoft }}>
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint, display: 'block' }}>
            Selected
          </Typography>
          <Typography variant="h6" sx={{ color: ELIG_TOKENS.ink, fontWeight: 700, lineHeight: 1.1 }}>
            {dollars(totalCents)}
          </Typography>
        </Box>
      </Box>

      <Box>
        {rows.map((row) => {
          const selectable = selectableIds.has(row.line.id);
          const selected = selectedIds.has(row.line.id);
          const lineLabel = receiptLineLabel(row.line);
          return (
            <Box
              key={row.line.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                gap: 1.25,
                alignItems: 'center',
                px: 2,
                py: 1.25,
                borderTop: `1px solid ${ELIG_TOKENS.line}`,
                bgcolor: selected ? ELIG_TOKENS.card : ELIG_TOKENS.bg,
                opacity: selectable ? 1 : 0.52,
              }}
            >
              <Checkbox
                checked={selected}
                disabled={!selectable}
                size="small"
                onChange={(event) => onToggleLine(row.line.id, event.target.checked)}
                slotProps={{ input: { 'aria-label': `Select ${lineLabel}` } }}
                sx={{ p: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: ELIG_TOKENS.ink,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lineLabel}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: ELIG_TOKENS.inkFaint,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {receiptLineSubtext(row)}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: ELIG_TOKENS.ink, fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {dollars(row.line.amountCents)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: ELIG_TOKENS.amberTint,
          borderTop: `1px solid ${ELIG_TOKENS.lineStrong}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 700 }}>
          Selected total
        </Typography>
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 700 }}>
          {dollars(totalCents)}
        </Typography>
      </Box>
    </Box>
  );
};

type SettlementPreviewRow = {
  group: DisplayEncounterChargeGroup;
  line: DisplayEncounterChargeGroup['lines'][number];
};

const flattenPreviewRows = (
  groups: DisplayEncounterChargeGroup[],
): SettlementPreviewRow[] =>
  groups.flatMap(group => group.lines.map(line => ({ group, line })));

const receiptLineLabel = (
  line: DisplayEncounterChargeGroup['lines'][number],
): string => {
  const code = normalizeCode(line.code);
  return (
    line.responsibilityLabel ??
    normalizeDescription(line.description, code) ??
    (code ? `CPT ${code}` : 'Charge')
  );
};

const receiptLineSubtext = ({ group, line }: SettlementPreviewRow): string => {
  const code = normalizeCode(line.code);
  const pieces = [
    group.serviceDate ? shortDate(group.serviceDate) : null,
    group.encounterTypeLabel,
    code && receiptLineLabel(line) !== `CPT ${code}` ? `CPT ${code}` : null,
    line.appliedPaymentCents && line.appliedPaymentCents > 0
      ? `paid ${dollars(line.appliedPaymentCents)}`
      : null,
  ].filter(Boolean);
  return pieces.join(' · ') || ' ';
};

// ─── Per-method morphing fields ──────────────────────────────────────────────

interface MethodFieldsProps {
  method: SettleMethod;
  isEncounterScope: boolean;
  paymentMethodKind: 'cash' | 'check';
  setPaymentMethodKind: (v: 'cash' | 'check') => void;
  externalKind: 'square' | 'venmo' | 'other_pos';
  setExternalKind: (v: 'square' | 'venmo' | 'other_pos') => void;
  referenceNumber: string;
  setReferenceNumber: (v: string) => void;
  collectedAt: string;
  setCollectedAt: (v: string) => void;
  note: string;
  setNote: (v: string) => void;
  invoiceReason: string;
  setInvoiceReason: (v: string) => void;
  mobileOverride: string;
  setMobileOverride: (v: string) => void;
  sendNotification: boolean;
  setSendNotification: (v: boolean) => void;
  writeOffReason: 'hardship' | 'bad_debt' | 'courtesy' | 'other';
  setWriteOffReason: (v: 'hardship' | 'bad_debt' | 'courtesy' | 'other') => void;
  savedCards: Array<{
    id: string;
    card_brand?: string | null;
    card_last4?: string | null;
    card_exp_month?: number | null;
    card_exp_year?: number | null;
    is_default?: boolean;
  }>;
  cardOnFileId: string;
  setCardOnFileId: (v: string) => void;
}

const MethodFields = (props: MethodFieldsProps) => {
  switch (props.method) {
    case 'card_on_file':
      return (
        <>
          {props.savedCards.length > 1 ? (
            <Field>
              <TextField
                select
                label="Card"
                value={props.cardOnFileId}
                onChange={(e) => props.setCardOnFileId(e.target.value)}
                fullWidth
                size="small"
              >
                {props.savedCards.map((card) => (
                  <MenuItem key={card.id} value={card.id}>
                    {formatCard(card)}
                  </MenuItem>
                ))}
              </TextField>
            </Field>
          ) : (
            <Helper>
              Will charge: <strong>{formatCard(props.savedCards[0])}</strong>. No SMS is sent.
            </Helper>
          )}
          <Field>
            <TextField
              label="Reason (shown on receipt)"
              value={props.invoiceReason}
              onChange={(e) => props.setInvoiceReason(e.target.value)}
              fullWidth
              size="small"
              placeholder="Office visit copay"
            />
          </Field>
        </>
      );
    case 'invoice':
      return (
        <>
          <Field>
            <TextField
              label="Mobile (optional override)"
              value={props.mobileOverride}
              onChange={(e) => props.setMobileOverride(e.target.value)}
              fullWidth
              size="small"
              placeholder="Uses patient.mobile_phone"
            />
          </Field>
          <Field>
            <TextField
              label="Reason (shown on invoice)"
              value={props.invoiceReason}
              onChange={(e) => props.setInvoiceReason(e.target.value)}
              fullWidth
              size="small"
              placeholder="Office visit copay"
            />
          </Field>
          <Field>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={props.sendNotification}
                  onChange={(e) => props.setSendNotification(e.target.checked)}
                />
              }
              label="Text the patient when sent"
            />
          </Field>
        </>
      );
    case 'remind_invoice':
      return (
        <Helper>Re-sends the existing Stripe invoice. No new invoice created.</Helper>
      );
    case 'void_invoice':
      return (
        <Helper>
          Voids the open invoice and frees its receipt lines. No replacement invoice is
          created.
        </Helper>
      );
    case 'cash':
      return (
        <>
          <Field>
            <TextField
              select
              label="Method"
              value={props.paymentMethodKind}
              onChange={(e) => props.setPaymentMethodKind(e.target.value as 'cash' | 'check')}
              fullWidth
              size="small"
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="check">Check</MenuItem>
            </TextField>
          </Field>
          <Field>
            <TextField
              label="Reference # (optional)"
              value={props.referenceNumber}
              onChange={(e) => props.setReferenceNumber(e.target.value)}
              fullWidth
              size="small"
              placeholder="Receipt or check #"
            />
          </Field>
          <Field>
            <TextField
              label="Note"
              value={props.note}
              onChange={(e) => props.setNote(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Field>
        </>
      );
    case 'external':
      return (
        <>
          <Field>
            <TextField
              select
              label="Source"
              value={props.externalKind}
              onChange={(e) =>
                props.setExternalKind(e.target.value as 'square' | 'venmo' | 'other_pos')
              }
              fullWidth
              size="small"
            >
              <MenuItem value="square">Square</MenuItem>
              <MenuItem value="venmo">Venmo</MenuItem>
              <MenuItem value="other_pos">Other POS</MenuItem>
            </TextField>
          </Field>
          <Field>
            <TextField
              label="Reference # (optional)"
              value={props.referenceNumber}
              onChange={(e) => props.setReferenceNumber(e.target.value)}
              fullWidth
              size="small"
              placeholder="Transaction id"
            />
          </Field>
          <Field>
            <TextField
              label="Collected at"
              type="datetime-local"
              value={props.collectedAt}
              onChange={(e) => props.setCollectedAt(e.target.value)}
              fullWidth
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Field>
          {props.isEncounterScope ? (
            <Helper>
              No charge_line yet on this encounter — we&apos;ll create one and apply the payment in
              one call (BE: <code>create_charge=true</code>).
            </Helper>
          ) : (
            <Helper>
              Patient-scope external collection lands as account-level credit. Open the drawer
              from a specific encounter to allocate.
            </Helper>
          )}
        </>
      );
    case 'write_off':
      return (
        <>
          <Field>
            <TextField
              select
              label="Reason"
              value={props.writeOffReason}
              onChange={(e) =>
                props.setWriteOffReason(
                  e.target.value as 'hardship' | 'bad_debt' | 'courtesy' | 'other',
                )
              }
              fullWidth
              size="small"
            >
              <MenuItem value="hardship">Hardship</MenuItem>
              <MenuItem value="bad_debt">Bad debt</MenuItem>
              <MenuItem value="courtesy">Courtesy</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Field>
          <Field>
            <TextField
              label="Note"
              value={props.note}
              onChange={(e) => props.setNote(e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Field>
        </>
      );
  }
};

// ─── helpers ────────────────────────────────────────────────────────────────

const SheetHeader = ({
  title,
  subtitle,
  onClose,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
}) => (
  <Box
    sx={{
      px: 3,
      py: 1.5,
      borderBottom: `1px solid ${ELIG_TOKENS.line}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ELIG_TOKENS.midnight }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    <Button onClick={onClose} size="small" sx={{ color: ELIG_TOKENS.inkSoft, textTransform: 'none' }}>
      Cancel
    </Button>
  </Box>
);

const Field = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>{children}</Box>
);

const Helper = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      color: ELIG_TOKENS.inkSoft,
      fontStyle: 'italic',
      bgcolor: ELIG_TOKENS.bg,
      p: 1,
      borderRadius: 0.5,
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const formatCard = (
  card:
    | {
        card_brand?: string | null;
        card_last4?: string | null;
        is_default?: boolean;
      }
    | undefined,
): string => {
  if (!card) return '—';
  const brand = card.card_brand
    ? card.card_brand.charAt(0).toUpperCase() + card.card_brand.slice(1)
    : 'Card';
  const last4 = card.card_last4 ?? '••••';
  return `${brand} •••• ${last4}${card.is_default ? ' (default)' : ''}`;
};

const buildSelectedInvoiceReason = (
  groups: DisplayEncounterChargeGroup[],
  selectedIds: Set<string>,
): string => {
  const encounterParts = new Map<string, Map<string, { label: string; code: string | null; amountCents: number }>>();
  for (const group of groups) {
    const selectedLines = group.lines.filter(line => selectedIds.has(line.id));
    if (selectedLines.length === 0) continue;
    const encounterLabel = [
      group.encounterTypeLabel,
      group.serviceDate ? `on ${shortDate(group.serviceDate)}` : null,
    ].filter(Boolean).join(' ') || 'Visit';
    const lineParts = encounterParts.get(encounterLabel) ?? new Map<string, {
      label: string;
      code: string | null;
      amountCents: number;
    }>();
    for (const line of selectedLines) {
      const code = normalizeCode(line.code);
      const label = receiptLineLabel(line);
      const key = `${label}|${code ?? ''}`;
      const existing = lineParts.get(key);
      lineParts.set(key, {
        label,
        code,
        amountCents: (existing?.amountCents ?? 0) + line.amountCents,
      });
    }
    encounterParts.set(encounterLabel, lineParts);
  }
  return Array.from(encounterParts.entries())
    .map(([encounterLabel, lineParts]) => {
      const responsibilityLabel = Array.from(lineParts.values())
        .map(selectedLineReasonLabel)
        .join(' + ');
      return [encounterLabel, responsibilityLabel].filter(Boolean).join(' - ');
    })
    .join('; ');
};

const selectedLineReasonLabel = ({
  label,
  code,
  amountCents,
}: {
  label: string;
  code: string | null;
  amountCents: number;
}): string =>
  `${label} ${dollars(amountCents)}${code && label !== `CPT ${code}` ? ` (CPT ${code})` : ''}`;

const dollars = (cents: number): string => {
  const abs = Math.abs(cents);
  if (abs % 100 === 0) return `$${abs / 100}`;
  return `$${(abs / 100).toFixed(2)}`;
};

const shortDate = (date: string): string => {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const normalizeCode = (code: string | null | undefined): string | null => {
  const normalized = code?.replace(/^CPT:/, '').replace(/^CPT\s+/, '').trim();
  return normalized || null;
};

const normalizeDescription = (
  description: string | null | undefined,
  code: string | null,
): string | null => {
  const normalized = description?.replace(/^CPT:/, '').replace(/^CPT\s+/, '').trim();
  if (!normalized || normalized === code) return null;
  return description ?? null;
};

const primaryFor = (method: SettleMethod): string => {
  switch (method) {
    case 'card_on_file':
      return 'Charge card';
    case 'invoice':
      return 'Send invoice';
    case 'remind_invoice':
      return 'Send reminder';
    case 'void_invoice':
      return 'Void invoice';
    case 'cash':
      return 'Record payment';
    case 'external':
      return 'Log collection';
    case 'write_off':
      return 'Write off';
  }
};
